import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { ResetPasswordDialogComponent } from "../reset-password-dialog/reset-password-dialog.component";

@Component({
  selector: "app-login-dialog",
  templateUrl: "./login-dialog.component.html",
  styleUrls: ["./login-dialog.component.scss"],
})
export class LoginDialogComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>();
  private resetPasswordDialogRef?: MatDialogRef<ResetPasswordDialogComponent>;

  constructor(
    public dialogRef: MatDialogRef<LoginDialogComponent>,
    private dialog: MatDialog,
    private router: Router
  ) {}

  /**
   * When opened on a reset link (/reset-password?token=...), e.g. because the stored tokens were
   * stale, also opens the reset password dialog on top with the link's code filled in, since the
   * reset page underneath is hidden by this dialog.
   */
  ngOnInit(): void {
    const urlTree = this.router.parseUrl(this.router.url);
    const path = urlTree.root.children["primary"]?.segments.map((s) => s.path).join("/");
    const token = urlTree.queryParams["token"];

    if (path === "reset-password" && token) {
      this.onForgotPassword(token);
    }
  }

  /**
   * Closes the reset password dialog opened from this one, if any, so it isn't left on its own
   * when this dialog closes (e.g. when the login flow is cancelled by ApiService.cancelVerifyTokens()),
   * and cleans up subscriptions.
   */
  ngOnDestroy(): void {
    this.resetPasswordDialogRef?.close();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Closes the dialog when the embedded login form reports a successful login.
   * @param event Whether the login attempt succeeded
   */
  public loginFormSubmit(event: boolean): void {
    if (event) {
      this.dialogRef.close();
    }
  }

  /**
   * Opens the reset password flow in a dialog on top of this one, so it isn't hidden behind it.
   * This dialog stays open underneath so the user can log in once the password has been reset.
   * @param code Optional reset code (e.g. from the reset link) to open the form directly on the code step
   */
  public onForgotPassword(code?: string): void {
    this.resetPasswordDialogRef = this.dialog.open(ResetPasswordDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: { code },
    });
  }
}
